import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { ICreateEmail, IEmail, ISendEmail, IUpdateEmail } from './types';

export const useGetEmails = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEmail[], DefaultError>({
    queryKey: ['emails'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Email/EmailConfigurations`,
      });

      return responseHandler(response, {
        groupName: `useGetEmails`,
      });
    },
    enabled: enabled,
  });
};

export const useGetEmail = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEmail, DefaultError>({
    queryKey: ['email', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Email/ById`,
        params: { Id: id },
      });

      return responseHandler(response, {
        groupName: `usetGetEmail - ${id}`,
      });
    },
    enabled: enabled,
  });
};

export const usePostEmail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostEmail');
  return useMutation<string, DefaultError, ICreateEmail>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('email.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Email/CreateEmailConfiguration',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostEmail',
        notFoundPath: '/email',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('email.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const useSendEmail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useSendEmail');
  return useMutation<string, DefaultError, ISendEmail>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.send.loading', {
          what: i18next.t('email.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Email/SendEmail',
        body,
      });

      return responseHandler(response, {
        groupName: 'useSendEmail',
        onNotFound: () => undefined,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.send.success', {
          what: i18next.t('email.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutEmail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutEmail');

  return useMutation<boolean, DefaultError, IUpdateEmail>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('email.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Email/UpdateEmailConfiguration',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutEmail',
        notFoundPath: '/email',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('email.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteEmail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteEmail');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('email.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Email/DeleteEmailConfiguration`,
        body: { id: id },
      });

      return responseHandler(response, {
        groupName: 'useDeleteEmail',
        notFoundPath: '/email',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('email.what'),
        })
      );
    },
    onError: dismiss,
  });
};
